import { getCompanyUsers, getDepartmentTree } from "@/api/userCenter/company"
import { mergeSet } from "@/utils/common"
import _ from "lodash"
import { getAllConnectedCompany } from "@/api/connect"
import { getAppUsers } from "@/api/user"
import store from "@/store"
import { bfs } from "@/utils/algorithm"

let users = []
let appUsers = []
let userMap = {}
let departmentMap = {}
let departmentLevelMap = {}
let companyMap = {}
let departmentIdAllUserIdSetMap = {}
let roleIdUserIdSetMap = {}

let last = {}

/**
 * 初始化用户信息
 * @param appId 工作表模块调用时必填,任务模块调用时不必填
 * @returns {Promise<Awaited<unknown>[]>}
 */
export function initUserData(appId = null) {
  if (!store.getters.userInfo.companyId) return Promise.resolve()
  return Promise.all([
    getCompanyUsers(),
    getDepartmentTree(),
    getAppUsers({ appId }),
    getAllConnectedCompany()
  ]).then(([companyUsers, departments, reqAppUsers, companies]) => {
    if (last.companyUsers !== companyUsers) {
      users = companyUsers.map(u => {
        if (u.companyScope?.gone && !u.name.endsWith("(已停用)")) u.name += "(已停用)"
        return u
      })
      userMap = _.keyBy(users, "id")
      last.companyUsers = companyUsers
    }

    if (last.departments !== departments) {
      departmentIdAllUserIdSetMap = calcDepartmentIdAllUserIdSetMap(departments[0], users)
      getDifferentLevelDepartmentMap(departments)
      last.departments = departments
    }

    if (last.reqAppUsers !== reqAppUsers) {
      appUsers = reqAppUsers
      roleIdUserIdSetMap = calcRoleIdUserIdSetMap(appUsers)
      last.reqAppUsers = reqAppUsers
    }

    if (last.companies !== companies) {
      companyMap = _.keyBy(companies, "id")
      last.companies = companies
    }
  })
}

export function getUser(id) {
  return userMap[id]
}

export function getLevelDepartmentIds(userId, level) {
  const departmentIds = getUser(userId)?.companyScope?.departmentIds || []
  return departmentIds.map(departmentId => {
    return departmentLevelMap[departmentId][level]?.id
  })
}

export function getDepartment(id) {
  return departmentMap[id]
}

export function getCompany(id) {
  return companyMap[id]
}

export function getUsers(userIds = []) {
  return _.uniq(userIds)
    .map(userId => userMap[userId])
    .filter(t => t)
}

export function getUserIdsByDepartment(departmentIds) {
  return departmentIds.flatMap(departmentId => {
    if (departmentId == "1") {
      // 全公司
      return Object.keys(userMap)
    }
    return Array.from(departmentIdAllUserIdSetMap[departmentId] || [])
  })
}

export function getUserIdsByRole(roleIds) {
  return roleIds.flatMap(roleId => {
    if (roleId == "100") {
      // 全部角色(即当前应用下的所有应用成员)
      return appUsers.map(u => u.id)
    }
    return Array.from(roleIdUserIdSetMap[roleId] || [])
  })
}

/**
 * 计算部门下所有用户map(包括子部门)
 * 顺便计算 departmentMap
 * @param department
 * @returns {{}}
 */
export function calcDepartmentIdAllUserIdSetMap(department, users) {
  if (!department) return {}
  const departmentIdUserIdSetMap = calcDepartmentIdUserIdSetMap(users)
  // 按广度优先顺序,把部门放入数组中
  let allDepartments = [department]
  let top = 0
  while (top < allDepartments.length) {
    let curDepartment = allDepartments[top++]
    if (!curDepartment.children) continue
    allDepartments = allDepartments.concat(curDepartment.children)
  }
  // 倒序遍历部门,计算部门人员集合
  let res = {}
  while (top > 0) {
    let curDepartment = allDepartments[--top]
    // 更新 departmentMap
    departmentMap[curDepartment.id] = curDepartment
    // 部门自身人员
    let currentUserSet = departmentIdUserIdSetMap[curDepartment.id] || new Set()
    for (const subDepartment of curDepartment.children || []) {
      // 添加子部门人员
      currentUserSet = mergeSet(currentUserSet, res[subDepartment.id] || new Set())
    }
    res[curDepartment.id] = currentUserSet
  }
  return res
}

/**
 * 计算部门当前用户map(不包含子部门)
 * @param users
 * @returns {{}}
 */
export function calcDepartmentIdUserIdSetMap(users) {
  let res = {}
  users.forEach(user => {
    user.companyScope?.departmentIds?.forEach(departmentId => {
      if (!res[departmentId]) {
        res[departmentId] = new Set([user.id])
      } else {
        res[departmentId].add(user.id)
      }
    })
  })
  return res
}

/**
 * 计算角色用户映射
 * @param users
 * @returns {{}}
 */
export function calcRoleIdUserIdSetMap(users) {
  let res = {}
  users
    .filter(u => !_.isEmpty(u.roleIds))
    .forEach(user => {
      user.roleIds.forEach(roleId => {
        if (!res[roleId]) {
          res[roleId] = new Set([user.id])
        } else {
          res[roleId].add(user.id)
        }
      })
    })
  return res
}

export function transTreeToArr(departmentTree) {
  if (!departmentTree) return []
  let res = departmentTree
  for (const department of departmentTree) {
    if (department.children) {
      res = res.concat(transTreeToArr(department.children))
    }
  }
  return res
}

export function getDifferentLevelDepartmentMap(departments) {
  bfs({
    arr: departments,
    childFuc: department => department.children,
    cbk: department => {
      const level = {}
      switch (department._deep) {
        case 0:
          break
        case 1:
          level[1] = department
          break
        case 2:
          level[1] = departmentLevelMap[department.parentId][1]
          level[2] = department
          break
        case 3:
          level[1] = departmentLevelMap[department.parentId][1]
          level[2] = departmentLevelMap[department.parentId][2]
          level[3] = department
          break
        case 4:
          level[1] = departmentLevelMap[department.parentId][1]
          level[2] = departmentLevelMap[department.parentId][2]
          level[3] = departmentLevelMap[department.parentId][3]
          level[4] = department
          break
        default:
          level[1] = departmentLevelMap[department.parentId][1]
          level[2] = departmentLevelMap[department.parentId][2]
          level[3] = departmentLevelMap[department.parentId][3]
          level[4] = departmentLevelMap[department.parentId][4]
          break
      }
      departmentLevelMap[department.id] = level
    }
  })
}
